import { apiResourceUrl } from "@ploy-lib/core";
import paramsToString, { BaseResource } from "./BaseResource";
import { Entity, SimpleResource } from "@rest-hooks/rest";

class ObjectVehicleDetails {
	readonly carMake?: string = undefined;
	readonly carModel?: string = undefined;
	readonly carEngine?: string = undefined;
	readonly yearModel?: string = undefined;
	readonly regNr?: string = undefined;
}

class FinancialDetails {
	readonly listPrice?: number = undefined;
	readonly carPrice?: number = undefined;
	readonly equity?: number = undefined;
	readonly equityPercent?: number = undefined;
	readonly loanAmount?: number = undefined;
	readonly loanType?: string = undefined;
	readonly termsTotal?: number = undefined;
	readonly termLengthType?: string = undefined;
	readonly totalMonths?: number = undefined;
	readonly totalYears?: number = undefined;
	readonly interestOnlyMonths?: number = undefined;
}

class RatesAndFeesDetails {
	readonly netTotalTermAmount?: number = undefined;
	readonly nominalInterestRate?: number = undefined;
	readonly actualInterestRate?: number = undefined;
	readonly establishmentFee?: number = undefined;
	readonly documentFee?: number = undefined;
	readonly termFee?: number = undefined;
}

class CustomerDetails {
	readonly commentFromCustomer?: string = undefined;
	readonly householdStatus?: string = undefined;
	readonly shouldSplitLoan?: string = undefined;
}

class PropertyDetails {
	readonly estimatedValue?: number = undefined;
	readonly jointDebt?: number = undefined;
	readonly useType?: string = undefined;
	readonly applicationPropertyId?: number = undefined;
	readonly areaScore?: number = undefined;
	readonly municipalityScore?: number = undefined;
	readonly estimateUncertaintyLevel?: number = undefined;
}

class DebtDetails {
	readonly loanType?: string = undefined;
	readonly amount?: number = undefined;
	readonly customNumber?: number = undefined;
	readonly applicationDebtId?: number = undefined;
}

class LoanType {
	readonly key?: string = undefined;
	readonly value?: string = undefined;
}

export interface FinancingInformationData {
	readonly applicationId?: number;

	readonly objectType?: string;
	readonly carType?: string;

	// For use of ObjectDTO
	readonly carObjectData?: ObjectVehicleDetails;

	// Main customer financial details
	readonly mainCustomerFinancialData?: FinancialDetails;

	// Cosigner financial details
	readonly coSignerFinancialData?: FinancialDetails;

	readonly splitLoan: boolean;

	// Rates and fees
	readonly ratesAndFeesData?: RatesAndFeesDetails;

	readonly customerData?: CustomerDetails;

	readonly propertyData?: PropertyDetails[];

	readonly debtData?: DebtDetails[];

	readonly loanTypeData?: LoanType[];
}

export class FinancingInformationResource
	extends BaseResource
	implements FinancingInformationData
{
	readonly applicationId?: number = undefined;

	readonly objectType?: string = undefined;
	readonly carType?: string = undefined;

	readonly carObjectData?: ObjectVehicleDetails = undefined;
	readonly mainCustomerFinancialData?: FinancialDetails = undefined;
	readonly coSignerFinancialData?: FinancialDetails = undefined;
	readonly splitLoan: boolean = false;
	readonly ratesAndFeesData?: RatesAndFeesDetails = undefined;
	readonly customerData?: CustomerDetails = undefined;
	readonly propertyData?: PropertyDetails[] = undefined;
	readonly debtData?: DebtDetails[] = undefined;
	readonly loanTypeData?: LoanType[] = undefined;

	pk(parent?: any, key?: string): string | undefined {
		return `${this.applicationId?.toString()}`;
	}

	static url<T extends typeof SimpleResource>(
		this: T,
		urlParams: Readonly<Record<string, any>>
	) {
		const { applicationId, ...searchParams } = urlParams;

		const url = super.url(urlParams);

		return `${url}`;
	}

	static urlRoot = apiResourceUrl("financing-information");
}
